import { useMemo } from 'react'
import { useFormState } from 'react-hook-form'

import { useFormWithZod } from '#/shared/lib/form'
import { EMPTY_CUSTOMER_DEFAULTS, useCustomerSchema } from './customer-schema'
import type { CustomerFormData } from './customer-schema'

interface UseCustomerFormParams {
  defaultValues?: Partial<CustomerFormData>
  onSubmit?: (data: CustomerFormData) => void | Promise<void>
}

export function useCustomerForm({
  defaultValues,
  onSubmit,
}: UseCustomerFormParams = {}) {
  const schema = useCustomerSchema()
  const values = useMemo<CustomerFormData>(
    () => ({ ...EMPTY_CUSTOMER_DEFAULTS, ...defaultValues }),
    [defaultValues],
  )
  const form = useFormWithZod(schema, {
    defaultValues: values,
    values,
    resetOptions: { keepDirtyValues: true },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })
  const { errors, dirtyFields } = useFormState({ control: form.control })
  const isDirty = Object.keys(dirtyFields).length > 0

  return {
    control: form.control,
    register: form.register,
    setValue: form.setValue,
    getValues: form.getValues,
    trigger: form.trigger,
    errors,
    isSubmitting: form.isSubmitting,
    isDirty,
    handleSubmit: onSubmit ? form.handleSubmit(onSubmit) : undefined,
    reset: form.reset,
  }
}
